"use client"

import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './auth-context'
import { functions } from '../lib/firebase'
import { httpsCallable } from 'firebase/functions'
import { onSnapshot, doc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { getLLMConfig } from '../shared/models'

const SubscriptionContext = createContext();

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [remainingChats, setRemainingChats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [modelUsage, setModelUsage] = useState({}); // Track monthly model usage counts
  const [lifetimeUsage, setLifetimeUsage] = useState({}); // Track lifetime model usage counts
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setRemainingChats(null);
      setSubscription(null);
      setModelUsage({});
      setLifetimeUsage({});
      setIsSubscriptionLoading(false);
      return;
    }

    // Set up listener for user document to get subscription status and model usage
    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid),
      (doc) => {
        if (doc.exists()) {
          const userData = doc.data();
          setRemainingChats(userData.remainingChats);
          setSubscription(userData.subscription);
          setModelUsage(userData.modelUsage || {});
          setLifetimeUsage(userData.lifetimeUsage || {});
        }
        setIsSubscriptionLoading(false);
      },
      (error) => {
        console.error("Error getting user subscription data:", error);
        setIsSubscriptionLoading(false);
      }
    );

    // Check if we need to reset monthly usage
    const checkMonthlyReset = async () => {
      try {
        const checkReset = httpsCallable(functions, 'checkMonthlyReset');
        const result = await checkReset();
        
        if (result.data?.success && result.data.data?.wasReset) {
          setRemainingChats(result.data.data.remainingChats);
        }
      } catch (error) {
        console.error("Error checking monthly reset:", error);
      }
    };

    checkMonthlyReset();

    return () => unsubscribe();
  }, [user]);

  // Helper function to determine if user can start a new chat
  const canMakeMoreChats = () => {
    // If still loading, assume they can chat to avoid flickering UI
    if (isSubscriptionLoading) return true;
    
    // If they have active subscription, they can chat
    if (subscription?.status === "active") return true;
    
    // Otherwise, check remaining free chats
    return remainingChats > 0;
  };

  // Helper to check if user has active subscription
  const hasActiveSubscription = () => {
    return subscription?.status === "active";
  };
  
  // Helper to check if model is available to the user
  const isModelAvailable = (modelId) => {
    if (!hasActiveSubscription()) {
      // Free users can only use non-pro models
      return !getLLMConfig()[modelId]?.requiresPro && !getLLMConfig()[modelId]?.requiresUltimate;
    }
    
    if (subscription.tier === 'ultimate') {
      // Ultimate users can use all models
      return true;
    }
    
    if (subscription.tier === 'pro') {
      // Pro users can use all non-ultimate models
      if (getLLMConfig()[modelId]?.requiresUltimate) return false;
      
      // For models with limited usage, check if they've reached their limit
      if (getLLMConfig()[modelId]?.limitedUsage) {
        const currentUsage = modelUsage[modelId] || 0;
        const limit = getLLMConfig()[modelId]?.monthlyLimit || 0;
        return currentUsage < limit;
      }
      
      return true;
    }
    
    return false;
  };

  const value = {
    remainingChats,
    subscription,
    modelUsage,
    lifetimeUsage,
    isSubscriptionLoading,
    canMakeMoreChats,
    hasActiveSubscription: hasActiveSubscription.bind(this), // To get updated subscription info in real time
    isModelAvailable,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}